const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// ============ USER WORKOUTS ============

export const getUserWorkouts = async () => {
  const response = await fetch(`${API_URL}/fitness/my-workouts/created`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to fetch workouts');
  return response.json();
};

export const createUserWorkout = async (data) => {
  const response = await fetch(`${API_URL}/fitness/my-workouts/created`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to create workout');
  return response.json();
};

export const updateUserWorkout = async (id, data) => {
  const response = await fetch(`${API_URL}/fitness/my-workouts/created/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to update workout');
  return response.json();
};

export const deleteUserWorkout = async (id, options = {}) => {
  const params = new URLSearchParams();
  if (options.cascadeExercises) params.set('cascadeExercises', '1');
  const qs = params.toString();
  const response = await fetch(
    `${API_URL}/fitness/my-workouts/created/${id}${qs ? `?${qs}` : ''}`,
    {
      method: 'DELETE',
      headers: getAuthHeaders()
    }
  );
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to delete workout');
  return response.json();
};

export const cloneWorkout = async (workoutId) => {
  const response = await fetch(`${API_URL}/fitness/workouts/${workoutId}/clone`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to clone workout');
  return response.json();
};

// ============ USER PROGRAMS ============

export const getUserPrograms = async () => {
  const response = await fetch(`${API_URL}/fitness/my-programs`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to fetch programs');
  return response.json();
};

export const createUserProgram = async (data) => {
  const response = await fetch(`${API_URL}/fitness/my-programs`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to create program');
  return response.json();
};

export const updateUserProgram = async (id, data) => {
  const response = await fetch(`${API_URL}/fitness/my-programs/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to update program');
  return response.json();
};

export const deleteUserProgram = async (id, options = {}) => {
  const params = new URLSearchParams();
  if (options.cascadeWorkouts) params.set('cascadeWorkouts', '1');
  if (options.cascadeExercises) params.set('cascadeExercises', '1');
  const qs = params.toString();
  const response = await fetch(`${API_URL}/fitness/my-programs/${id}${qs ? `?${qs}` : ''}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to delete program');
  return response.json();
};

export const cloneProgram = async (programId) => {
  const response = await fetch(`${API_URL}/fitness/programs/${programId}/clone`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to clone program');
  return response.json();
};

// ============ USER EXERCISES ============

export const getUserExercises = async () => {
  const response = await fetch(`${API_URL}/fitness/my-exercises`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to fetch exercises');
  return response.json();
};

export const createUserExercise = async (data) => {
  const response = await fetch(`${API_URL}/fitness/my-exercises`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to create exercise');
  return response.json();
};

export const updateUserExercise = async (id, data) => {
  const response = await fetch(`${API_URL}/fitness/my-exercises/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to update exercise');
  return response.json();
};

export const deleteUserExercise = async (id) => {
  const response = await fetch(`${API_URL}/fitness/my-exercises/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to delete exercise');
  return response.json();
};

/**
 * POST multipart FormData with field "images" — AI + QR extraction.
 * @param {FormData} formData
 * @returns {Promise<{ status: string, exercises: object[], decodedQrUrls?: string[], confidence?: object, usage?: object }>}
 */
export const extractExercisesFromImages = async (formData) => {
  const token = localStorage.getItem('token');
  const url = `${API_URL}/fitness/extract-exercises-from-images`;

  const uploadWithXHR = () =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch (e) {
            reject(new Error('Invalid response from server'));
          }
        } else {
          try {
            const err = JSON.parse(xhr.responseText);
            reject(new Error(err.message || `Upload failed with status ${xhr.status}`));
          } catch (e) {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        }
      };
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.ontimeout = () => reject(new Error('Request timed out'));
      xhr.timeout = 120000;
      xhr.send(formData);
    });

  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  if (isSafari) {
    return uploadWithXHR();
  }
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Failed to extract exercises');
    return data;
  } catch (fetchError) {
    if (fetchError.message === 'Load failed' || fetchError.message === 'Failed to fetch') {
      return uploadWithXHR();
    }
    throw fetchError;
  }
};

export const cloneExercise = async (exerciseId) => {
  const response = await fetch(`${API_URL}/fitness/exercises/${exerciseId}/clone`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to clone exercise');
  return response.json();
};

// ============ PUBLIC (re-exports for convenience) ============

export const getExercises = async (params = {}) => {
  const query = new URLSearchParams(params).toString();
  const response = await fetch(`${API_URL}/fitness/exercises${query ? `?${query}` : ''}`);
  if (!response.ok) throw new Error('Failed to fetch exercises');
  return response.json();
};

export const getExercisesPaginated = async (params = {}) => {
  const query = new URLSearchParams(params).toString();
  const headers = {};
  const token = localStorage.getItem('token');
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(`${API_URL}/fitness/exercises-paginated${query ? `?${query}` : ''}`, { headers });
  if (!response.ok) throw new Error('Failed to fetch exercises');
  return response.json();
};

export const getExerciseFilters = async () => {
  const headers = {};
  const token = localStorage.getItem('token');
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(`${API_URL}/fitness/exercises-filters`, { headers });
  if (!response.ok) throw new Error('Failed to fetch filters');
  return response.json();
};

export const getWorkouts = async (params = {}) => {
  const query = new URLSearchParams(params).toString();
  const headers = {};
  const token = localStorage.getItem('token');
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(`${API_URL}/fitness/workouts${query ? `?${query}` : ''}`, { headers });
  if (!response.ok) throw new Error('Failed to fetch workouts');
  return response.json();
};

export const getPrograms = async (params = {}) => {
  const query = new URLSearchParams(params).toString();
  const headers = {};
  const token = localStorage.getItem('token');
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(`${API_URL}/fitness/programs${query ? `?${query}` : ''}`, { headers });
  if (!response.ok) throw new Error('Failed to fetch programs');
  return response.json();
};

// ============ SCHEDULING ============

export const scheduleWorkout = async ({ type, workoutId, programId, scheduledDate, notes }) => {
  const response = await fetch(`${API_URL}/fitness/my-schedule`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ type, workoutId, programId, scheduledDate, notes })
  });
  const data = await response.json();
  if (!response.ok) {
    const err = new Error(data.error || 'Failed to schedule workout');
    err.data = data;
    throw err;
  }
  return data;
};

// ============ PROGRAM IMPORT (Trainerize PDF) ============

export const previewProgramImport = async (file, options = {}) => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('You must be logged in');
  const source = options.source || 'auto';
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(
    `${API_URL}/fitness/import-program-preview?source=${encodeURIComponent(source)}`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData
    }
  );
  const data = await res.json();
  if (!res.ok) {
    const err = new Error(data.message || 'Preview failed');
    err.details = data;
    throw err;
  }
  return data;
};

export const commitProgramImport = async (bundle, options = {}) => {
  const response = await fetch(`${API_URL}/fitness/import-program-commit`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      bundle,
      linkToExistingExercises: options.linkToExistingExercises !== false
    })
  });
  const data = await response.json();
  if (!response.ok) {
    const err = new Error(data.message || 'Import failed');
    err.upgrade = !!data.upgrade;
    err.upgradeUrl = data.upgradeUrl || '/pricing#pricing-personal';
    err.upgradeHint = data.upgradeHint;
    throw err;
  }
  return data;
};

// ============ LIMITS ============

export const getMyLimits = async () => {
  const response = await fetch(`${API_URL}/fitness/my-limits`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to fetch limits');
  return response.json();
};

// ============ PROGRESSION ============

export const getProgressionSummary = async () => {
  const response = await fetch(`${API_URL}/fitness/progression/summary`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to fetch progression summary');
  return response.json();
};

export const getExerciseProgression = async (exerciseId) => {
  const response = await fetch(`${API_URL}/fitness/progression/${encodeURIComponent(exerciseId)}`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error('Failed to fetch exercise progression');
  return response.json();
};
